import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

interface UseMemberImportNavigationGuardOptions {
  active: boolean;
}

/**
 * Blocks refresh/close and in-app link navigation while an import runs.
 * BrowserRouter does not support useBlocker (data router only), so internal
 * navigation is intercepted at click capture instead.
 */
export const useMemberImportNavigationGuard = ({
  active,
}: UseMemberImportNavigationGuardOptions) => {
  const navigate = useNavigate();
  const [leaveOpen, setLeaveOpen] = useState(false);
  const pendingPathRef = useRef<string | null>(null);
  const pendingBackRef = useRef(false);

  useEffect(() => {
    if (!active) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [active]);

  useEffect(() => {
    if (!active) return;

    window.history.pushState({ memberImportGuard: true }, '');

    const onPopState = () => {
      window.history.pushState({ memberImportGuard: true }, '');
      pendingBackRef.current = true;
      pendingPathRef.current = null;
      setLeaveOpen(true);
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [active]);

  useEffect(() => {
    if (!active) return;

    const onClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const target = event.target;
      if (!(target instanceof Element)) return;

      const anchor = target.closest('a[href]');
      if (!(anchor instanceof HTMLAnchorElement) || anchor.target === '_blank' || anchor.hasAttribute('download')) {
        return;
      }

      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) {
        return;
      }

      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin) return;

      const nextPath = `${url.pathname}${url.search}${url.hash}`;
      const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      if (nextPath === currentPath) return;

      event.preventDefault();
      event.stopPropagation();
      pendingBackRef.current = false;
      pendingPathRef.current = nextPath;
      setLeaveOpen(true);
    };

    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [active]);

  const stay = useCallback(() => {
    pendingPathRef.current = null;
    pendingBackRef.current = false;
    setLeaveOpen(false);
  }, []);

  const leave = useCallback(() => {
    const pendingPath = pendingPathRef.current;
    const pendingBack = pendingBackRef.current;
    pendingPathRef.current = null;
    pendingBackRef.current = false;
    setLeaveOpen(false);

    if (pendingBack) {
      window.history.go(-2);
      return;
    }

    if (pendingPath) {
      void navigate(pendingPath);
    }
  }, [navigate]);

  return {
    leaveOpen,
    stay,
    leave,
  };
};
